import { useMemo } from "react";
import { fmtDate, ordinal, todayStr, uid } from "./format";
import {
  statusAfterCallback,
  statusAfterConfirm,
  statusAfterIntro,
  statusAfterReview,
} from "./jobStatus";
import { clone } from "./McOpsContext";
import { DRIVER_ASSIGNABLE, DRIVER_STATUS, fmtTime } from "./driverDelivery";
import { attemptLabel, inProgressNotesText, postMoveNotesText } from "./milestoneNotes";
import {
  confirmSitError,
  defaultCheckDate,
  deliveryCompleteBlocker,
  moveToDeliveryDayError,
  postMoveStatusFor,
  sitConfirmedDate,
} from "./sitLocal";

/**
 * Every job/escalation/reminder mutation from the approved prototype, rewritten as
 * immutable updates. Each one produces the next snapshot and hands it to `persist`.
 */
export const useMcOpsActions = (state, persist, effects = {}) =>
  useMemo(() => {
    const mutate = (fn) => {
      const next = clone(state);
      fn(next);
      persist(next);
      return next;
    };

    const job = (draft, id) => draft.jobs.find((j) => j.id === id);

    const logInternal = (draft, type, text, jobId) =>
      draft.notifInternal.unshift({
        id: uid(),
        type,
        text,
        jobId,
        date: new Date().toISOString(),
      });

    const logExternal = (draft, channel, text, jobId, unattended) =>
      draft.notifExternal.unshift({
        id: uid(),
        channel,
        text,
        jobId,
        date: new Date().toISOString(),
        unattended,
      });

    const pushNote = (draft, j, text, author = "MC") =>
      j.notes.push({ author, text, date: new Date().toISOString() });

    const pmIssue = (j, issueId) =>
      j && Array.isArray(j.pmIssues) ? j.pmIssues.find((i) => i.id === issueId) : undefined;

    const pushPmNote = (j, text) =>
      j.notes.push({ author: "MC", text, date: new Date().toISOString(), kind: "pm-issue" });

    // The Reminders page reads state.reminders, so an issue reminder is mirrored there.
    const pushPmReminder = (draft, j, issueId, text, date, time, note) => {
      draft.reminders.push({
        id: uid(),
        kind: "pm-issue",
        issueId,
        jobId: j.id,
        jobLabel: j.zohoId,
        text: "Post-move issue reminder: " + (note || text),
        date,
        time: time || "",
        scope: date === todayStr() ? "today" : "tomorrow",
      });
      logInternal(
        draft,
        "reminder",
        "Reminder set for post-move issue on " + j.zohoId + " (" + fmtDate(date) + ")",
        j.id,
      );
    };

    // A reminder is general (no job) or attached to a job; a job reminder is
    // also listed on the job itself. `time` is "HH:MM" from the picker, optional.
    const pushReminder = (d, j, { date, time, text }) => {
      const dt = date || todayStr();
      const tm = time || "";
      d.reminders.push({
        id: uid(),
        jobId: j ? j.id : null,
        jobLabel: j ? j.zohoId : "General",
        text,
        date: dt,
        time: tm,
        scope: dt === todayStr() ? "today" : "tomorrow",
      });
      if (j) j.reminders.push({ text, date: dt, time: tm });
      logInternal(
        d,
        "reminder",
        "Reminder set" + (j ? " on " + j.zohoId : "") + ": " + text,
        j ? j.id : null,
      );
    };

    const addReminder = ({ jobId, date, time, text }) =>
      mutate((d) => {
        const j = jobId ? job(d, jobId) : null;
        pushReminder(d, j, { date, time, text });
      });

    const sync = (j, event, text) => {
      if (effects.syncJobEvent && j && j.leadId)
        effects.syncJobEvent(text ? { leadId: j.leadId, event, text } : { leadId: j.leadId, event });
    };

    // s2Notes may arrive as [] from the backend: setting a named key on it is lost on save.
    const s2 = (j) => {
      if (!j.s2Notes || Array.isArray(j.s2Notes)) j.s2Notes = {};
      return j.s2Notes;
    };

    // SIT Local log entries, newest first (In Transit, design of 2026-10-24).
    const pushSitLog = (j, text, author = "MC") => {
      j.sitLog = Array.isArray(j.sitLog) ? j.sitLog : [];
      j.sitLog.unshift({ text, author, date: new Date().toISOString() });
    };

    const SIT_CHECK_REMINDER = /^Check with Operations/;
    const sitCheckReminderText = (j) =>
      "Check with Operations: is a crew assigned for the SIT Local delivery on " +
      fmtDate(sitConfirmedDate(j)) +
      "?";

    // The Operations check-in reminder follows the check-in date: one per job, moved, not duplicated.
    const setSitCheckReminder = (d, j, date) => {
      const existing = d.reminders.find(
        (r) => r.jobId === j.id && !r.completed && SIT_CHECK_REMINDER.test(r.text || ""),
      );
      if (existing) {
        existing.date = date;
        existing.text = sitCheckReminderText(j);
        existing.scope = date === todayStr() ? "today" : "tomorrow";
        (j.reminders || []).forEach((r) => {
          if (SIT_CHECK_REMINDER.test(r.text || "") && !r.completed) {
            r.date = date;
            r.text = existing.text;
          }
        });
        return;
      }
      pushReminder(d, j, { date, text: sitCheckReminderText(j) });
    };

    const completeSitCheckReminder = (d, j) => {
      d.reminders.forEach((r) => {
        if (r.jobId === j.id && SIT_CHECK_REMINDER.test(r.text || "")) r.completed = true;
      });
      (j.reminders || []).forEach((r) => {
        if (SIT_CHECK_REMINDER.test(r.text || "")) r.completed = true;
      });
    };

    // Confirming SIT Local: the job enters SIT Local Confirmed (unless it is
    // already with a driver or on Delivery Day, where it stays). With a delivery
    // date confirmed, the Operations check-in is set 2 days before it.
    const applySitConfirmed = (d, j) => {
      j.sitLocalConfirmed = true;
      j.sitLocalConfirmedAt = new Date().toISOString();
      if (j.status !== DRIVER_STATUS && j.status !== "ld-delivery-local") {
        j.status = "ld-sit-confirmed";
      }
      const delivery = sitConfirmedDate(j);
      let details = "";
      if (delivery) {
        const check = defaultCheckDate(delivery);
        const n = s2(j);
        n.sitOpsCheckDate = check;
        n.sitCheckDoneAt = "";
        setSitCheckReminder(d, j, check);
        details = "Delivery " + fmtDate(delivery) + ", Operations check-in " + fmtDate(check) + ".";
        pushSitLog(j, "SIT Local confirmed for " + fmtDate(delivery) + ". Operations check-in set for " + fmtDate(check) + ".");
      }
      pushNote(d, j, "SIT Local confirmed.");
      logInternal(d, "note", "SIT Local confirmed on " + j.zohoId + (details ? " — " + details : ""), j.id);
      sync(j, "sit-local-confirmed", details);
    };

    return {
      // ---- generic field writers -------------------------------------------
      setJobField: (id, key, value) =>
        mutate((d) => {
          const j = job(d, id);
          if (j) j[key] = value;
        }),

      setS2Note: (id, key, value) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          // A job with no saved override arrives with s2Notes = [] (empty array
          // from the backend). `[] || {}` is still the array, and setting a
          // named prop on an array is dropped by JSON.stringify — so the FIRST
          // note on any job silently vanished. Force a plain object first.
          if (!j.s2Notes || Array.isArray(j.s2Notes)) j.s2Notes = {};
          j.s2Notes[key] = value;
        }),

      toggleYN: (id, key, value) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          if (!j.s2YN || Array.isArray(j.s2YN)) j.s2YN = {}; // same []-vs-{} trap
          j.s2YN[key] = j.s2YN[key] === value ? "" : value;
        }),

      toggleRisk: (id, risk, checked) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.riskFlags = j.riskFlags || [];
          if (checked) {
            if (!j.riskFlags.includes(risk)) {
              j.riskFlags.push(risk);
              // Tell the salesperson (lead owner) about the risk, via the backend (Cliq DM + Zoho note).
              if (effects.syncJobEvent && j.leadId)
                effects.syncJobEvent({ leadId: j.leadId, event: "risk-flagged", text: risk });
            }
          } else {
            j.riskFlags = j.riskFlags.filter((x) => x !== risk);
          }
        }),

      // ---- notes / reminders / calls ---------------------------------------
      addNote: (id, text) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          pushNote(d, j, text);
          logInternal(d, "note", "Note added on " + j.zohoId + ": " + text, id);
          // Always attempt the sync so the user gets feedback — syncJobEvent
          // reports "dashboard only" when the job has no Zoho lead behind it.
          if (effects.syncJobEvent)
            effects.syncJobEvent({ leadId: j.leadId || null, event: "note", text });
        }),

      addReminder,

      addJobReminder: (id, text, date, time) => {
        if (job(state, id)) addReminder({ jobId: id, date, time, text });
      },

      logCall: (id) =>
        mutate((d) => {
          const j = job(d, id);
          if (j)
            j.callLog.push({
              summary: "Call completed with client.",
              date: new Date().toISOString(),
            });
        }),

      callClient: (id) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          logExternal(
            d,
            "outbound-call",
            "Outbound call attempted to " + j.clientName + " (" + j.zohoId + ")",
            id,
            false,
          );
          // Calling a client in a post-move status counts as the post-move call.
          if (String(j.status || "").startsWith("post-move") && !j.postMoveCallDone) {
            j.postMoveCallDone = true;
            j.postMoveCallDoneAt = new Date().toISOString();
          }
          if (effects.placeCall && j.phone) effects.placeCall(j.phone);
        }),

      // The MC spoke with the client after the move (outside the Talkdesk button).
      completePostMoveCall: (id) => {
        const current = job(state, id);
        if (!current || current.postMoveCallDone) return;
        mutate((d) => {
          const j = job(d, id);
          j.postMoveCallDone = true;
          j.postMoveCallDoneAt = new Date().toISOString();
          pushNote(d, j, "Post-move call completed.");
          logInternal(d, "status", "Post-move call completed on " + j.zohoId, id);
        });
      },

      // ---- status transitions ----------------------------------------------
      markIntro: (id) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.introCallDone = true;
          j.status = statusAfterIntro(j);
          pushNote(d, j, "Introduction call marked done.");
          if (effects.syncJobEvent && j.leadId)
            effects.syncJobEvent({ leadId: j.leadId, event: "intro-done" });
        }),

      // The MC finished the move-in-progress call: the job gets the tag and moves
      // from Moves in Progress to Move in Progress Completed. Zoho gets the MIP (LD:
      // MIP / APF) note with what was typed on the screen; unsaved client feedback
      // passed in is saved with it.
      completeInProgressCall: (id, feedback) => {
        const current = job(state, id);
        if (!current || current.inProgressCallDone) return;
        mutate((d) => {
          const j = job(d, id);
          if (typeof feedback === "string" && feedback.trim()) j.inProgressFeedback = feedback;
          j.inProgressCallDone = true;
          j.inProgressCallDoneAt = new Date().toISOString();
          pushNote(d, j, "In-progress call completed.");
          logInternal(d, "status", "In-progress call completed on " + j.zohoId, id);
          sync(j, "in-progress-call-done", inProgressNotesText(j, d.escalations));
        });
      },

      confirmJob: (id) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.confirmCallDone = true;
          j.status = statusAfterConfirm(j);
          pushNote(d, j, "Job confirmed.");
          if (effects.syncJobEvent && j.leadId)
            effects.syncJobEvent({ leadId: j.leadId, event: "confirmed" });
        }),

      // The MC called the client about a sales update: log the reason in the
      // notes and the Confirmation columns, and clear the job from Revised
      // Confirmations. The backend ignores changes made before this moment.
      markRevisionReviewed: (id, reason) => {
        const text = String(reason || "").trim();
        if (!text) return;
        return mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.revisionReviewedAt = new Date().toISOString();
          j.revisions = [];
          j.status = statusAfterReview(j);
          pushNote(d, j, "Reviewed change with client: " + text);
          if (!j.s2Notes || Array.isArray(j.s2Notes)) j.s2Notes = {}; // same []-vs-{} trap
          j.s2Notes.revisionReviewNote = text;
          logInternal(d, "note", "Change reviewed with client on " + j.zohoId + ": " + text, id);
        });
      },

      // Callback requested holds the job in Ready to Confirm; withdrawn, it
      // goes back to the queue its calls had left it in.
      setCallbackRequested: (id, requested) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.callBackRequested = !!requested;
          j.status = statusAfterCallback(j);
        }),

      // The "SIT Local Confirmed" button on the In Transit screen. It keeps the
      // etaConfirmed flag the footer button hides on. Returns why it cannot be
      // confirmed yet ("" when it was): a delivery date confirmed with the customer,
      // matching the Operations date and not in the past (design of 2026-10-24).
      markEtaConfirmed: (id) => {
        const current = job(state, id);
        if (!current) return "";
        const error = confirmSitError(current);
        if (error) return error;
        mutate((d) => {
          const j = job(d, id);
          j.etaConfirmed = true;
          applySitConfirmed(d, j);
        });
        return "";
      },

      // ---- SIT Local: dates, Operations check-in, log (design of 2026-10-24) -------
      // key: sitOpsDate | sitConfirmedDate. Changing the confirmed date of a confirmed
      // job moves its check-in (2 days before) and the reminder with it.
      setSitDate: (id, key, value) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j || (key !== "sitOpsDate" && key !== "sitConfirmedDate")) return;
          const n = s2(j);
          if ((n[key] || "") === (value || "")) return;
          n[key] = value || "";
          pushSitLog(
            j,
            (key === "sitOpsDate" ? "Operations date set to " : "Date confirmed with the customer: ") +
              (value ? fmtDate(value) : "cleared"),
          );
          if (key === "sitConfirmedDate" && value && j.status === "ld-sit-confirmed") {
            const check = defaultCheckDate(value);
            n.sitOpsCheckDate = check;
            n.sitCheckDoneAt = "";
            setSitCheckReminder(d, j, check);
            pushSitLog(j, "Operations check-in moved to " + fmtDate(check) + ".");
          }
        }),

      setSitCheckDate: (id, date) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j || !date) return;
          s2(j).sitOpsCheckDate = date;
          setSitCheckReminder(d, j, date);
          pushSitLog(j, "Operations check-in date changed to " + fmtDate(date) + ".");
        }),

      // "Confirm check-in" toggles: done (the reminder is completed) / reopened.
      confirmSitCheckin: (id) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          const n = s2(j);
          n.sitCheckDoneAt = n.sitCheckDoneAt ? "" : new Date().toISOString();
          const text = n.sitCheckDoneAt ? "Operations check-in completed." : "Operations check-in reopened.";
          pushSitLog(j, text);
          if (n.sitCheckDoneAt) completeSitCheckReminder(d, j);
          logInternal(d, "note", "SIT Local " + text.toLowerCase().replace(/\.$/, "") + " on " + j.zohoId, id);
          if (n.sitCheckDoneAt) sync(j, "sit-ops-checkin", "Check-in completed" + (n.sitCrewOk === "yes" ? ", crew confirmed." : ", crew not confirmed yet."));
        }),

      // "Operations confirms a crew": yes | no (pressed again, cleared).
      setSitCrew: (id, value) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j || (value !== "yes" && value !== "no")) return;
          const n = s2(j);
          n.sitCrewOk = n.sitCrewOk === value ? "" : value;
          n.sitCrewConfirmedAt = n.sitCrewOk === "yes" ? new Date().toISOString() : "";
          const text =
            n.sitCrewOk === "yes"
              ? "Operations confirmed a crew is assigned."
              : n.sitCrewOk === "no"
              ? "Operations has not assigned a crew yet — follow up."
              : "Crew confirmation cleared.";
          pushSitLog(j, text);
          if (n.sitCrewOk === "yes") completeSitCheckReminder(d, j);
          if (n.sitCrewOk) sync(j, "sit-ops-checkin", text + (n.sitCrewNote ? " " + n.sitCrewNote : ""));
        }),

      addSitLog: (id, text) => {
        const t = String(text || "").trim();
        if (!t) return;
        return mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          pushSitLog(j, t);
          pushNote(d, j, "SIT Local: " + t);
          logInternal(d, "note", "SIT Local log entry on " + j.zohoId + ": " + t, id);
          sync(j, "note", "SIT Local: " + t);
        });
      },

      addSitFeedback: (id, text) => {
        const t = String(text || "").trim();
        if (!t) return;
        return mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.sitFeedbackLog = Array.isArray(j.sitFeedbackLog) ? j.sitFeedbackLog : [];
          j.sitFeedbackLog.push({ text: t, author: "MC", date: new Date().toISOString() });
          pushNote(d, j, "SIT Local customer feedback: " + t);
          logInternal(d, "note", "SIT Local customer feedback saved on " + j.zohoId + ": " + t, id);
          sync(j, "note", "SIT Local customer feedback: " + t);
        });
      },

      // SIT Local Confirmed -> Delivery Day in Progress, once a crew is on the delivery.
      // Returns why it cannot move yet ("" when it moved).
      moveToDeliveryDay: (id) => {
        const current = job(state, id);
        if (!current) return "";
        const error = moveToDeliveryDayError(current);
        if (error) return error;
        mutate((d) => {
          const j = job(d, id);
          j.movedToDeliveryDayAt = new Date().toISOString();
          j.deliveryDayCompleted = false;
          j.status = "ld-delivery-local";
          completeSitCheckReminder(d, j);
          pushSitLog(j, "Crew confirmed — job moved to Delivery Day in Progress.");
          pushNote(d, j, "Moved to Delivery Day in Progress.");
          logInternal(d, "note", "Moved to Delivery Day: " + j.zohoId, id);
          sync(j, "moved-to-delivery-day", (j.s2Notes && j.s2Notes.sitCrewNote) || "");
        });
        return "";
      },

      // ---- ETA Communication (LD in transit) ----------------------------------
      // kind: "ld" (update from Long Distance) | "customer" (told to the customer —
      // that one is also an "ETA update" note on the Zoho lead, Asana 1219442712187460).
      addEtaLog: (id, kind, text) => {
        const t = String(text || "").trim();
        if (!t) return;
        const k = kind === "customer" ? "customer" : "ld";
        return mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.etaLog = Array.isArray(j.etaLog) ? j.etaLog : [];
          j.etaLog.unshift({ text: t, kind: k, author: "MC", date: new Date().toISOString() });
          pushNote(d, j, "ETA " + (k === "customer" ? "told to customer" : "update from Long Distance") + ": " + t);
          logInternal(d, "note", "ETA update logged on " + j.zohoId + ": " + t, id);
          // Both kinds go to Zoho: the customer update as before, the LD update as a note (Asana 1220099429071097, 5.4).
          if (k === "customer") sync(j, "eta-update", t);
          else sync(j, "note", "ETA update from Long Distance: " + t);
        });
      },

      // The job enters the SIT Local Confirmed column (button or drag from SIT Local).
      confirmSitLocal: (id) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          applySitConfirmed(d, j);
        }),

      // ---- Driver Deliveries -------------------------------------------------
      // details = { etaDate (required), etaTime, driverName, note, reminderText, reminderDate }
      assignDriverDelivery: (id, details) => {
        const { etaDate, etaTime, driverName, note, reminderText, reminderDate } = details || {};
        if (!etaDate || !job(state, id)) return;
        return mutate((d) => {
          const j = job(d, id);
          const time = etaTime || "";
          const driver = String(driverName || "").trim();
          const previous =
            j.status === DRIVER_STATUS
              ? (j.driverDelivery && j.driverDelivery.prevStatus) || "ld-in-transit"
              : j.status;
          j.driverDelivery = {
            etaDate,
            etaTime: time,
            driverName: driver,
            note: String(note || "").trim(),
            assignedAt: new Date().toISOString(),
            prevStatus: previous,
          };
          j.driverDelivered = false;
          j.status = DRIVER_STATUS;
          pushNote(
            d,
            j,
            "Assigned to Driver Delivery — ETA " +
              fmtDate(etaDate) +
              (time ? " at " + fmtTime(time) : "") +
              (driver ? ", driver " + driver : "") +
              ".",
          );
          logInternal(d, "note", "Driver delivery assigned on " + j.zohoId + " (ETA " + fmtDate(etaDate) + ")", id);
          const text = String(reminderText || "").trim();
          // The reminder lands on the earliest delivery date unless another is given.
          if (text) pushReminder(d, j, { date: reminderDate || etaDate, text });
        });
      },

      markDriverDelivered: (id) => {
        const current = job(state, id);
        if (!current) return;
        return mutate((d) => {
          const j = job(d, id);
          j.driverDelivered = true;
          j.driverDeliveredAt = new Date().toISOString();
          j.deliveryDayCompleted = false;
          j.status = "ld-delivery-local";
          pushNote(d, j, "Driver delivery completed — job moved to Delivery Day.");
          logInternal(d, "note", "Driver delivered " + j.zohoId, id);
        });
      },

      unassignDriverDelivery: (id) => {
        const current = job(state, id);
        if (!current) return;
        return mutate((d) => {
          const j = job(d, id);
          const previous = j.driverDelivery && j.driverDelivery.prevStatus;
          j.status = DRIVER_ASSIGNABLE.includes(previous) ? previous : "ld-in-transit";
          j.driverDelivery = null;
          pushNote(d, j, "Removed from Driver Deliveries.");
          logInternal(d, "note", "Removed from Driver Deliveries: " + j.zohoId, id);
        });
      },

      // LD Delivery Day "Mark Completed": the job goes straight to Post Move
      // (Miles, Asana 1219141409140286). Locked until the delivery BOL is signed /
      // the driver delivered / the delivery date has come, and until the client's
      // feedback is written (passed in, saved with it). Returns why it is locked, "" when done.
      markDeliveryDayCompleted: (id, feedback) => {
        const current = job(state, id);
        if (!current || current.deliveryDayCompletedAt) return "";
        const text = typeof feedback === "string" ? feedback : current.inProgressFeedback || "";
        const blocker = deliveryCompleteBlocker(current, text);
        if (blocker) return blocker;
        mutate((d) => {
          const j = job(d, id);
          j.inProgressFeedback = text.trim();
          j.deliveryDayCompleted = true;
          j.deliveryDayCompletedAt = new Date().toISOString();
          j.status = postMoveStatusFor(j);
          pushNote(d, j, "Delivery Day marked completed — moved to Post Move.");
          logInternal(d, "note", "Delivery Day completed on " + j.zohoId, id);
          sync(j, "delivery-completed", "Feedback from the client: " + text.trim());
        });
        return "";
      },

      closePostMove: (id) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.postMoveClosed = true;
          pushNote(d, j, "Post Move marked completed.");
          logInternal(d, "note", "Post Move completed on " + j.zohoId, id);
          sync(j, "closed", postMoveNotesText(j));
        }),

      generatePdf: (id) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.pdfGenerated = true;
          pushNote(
            d,
            j,
            "Confirmation PDF (screens 1-3) generated and attached to Zoho.",
          );
          if (effects.generateConfirmationPdf) effects.generateConfirmationPdf(j);
        }),

      startAutomatedFlow: (id) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.automatedFlowStarted = true;
          pushNote(d, j, "Marked for the automated post-move check-in. Nothing is sent yet, so call the client as usual.");
          logInternal(d, "note", "Marked for the automated post-move check-in on " + j.zohoId, id);
        }),

      requestReview: (id, target) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          pushNote(d, j, "Review requested — " + target + " feedback.");
          logInternal(d, "note", "Review requested (" + target + ") on " + j.zohoId, id);
        }),

      // ---- outreach attempts -------------------------------------------------
      saveAttempt: (id, status, feedback) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.introAttempts = j.introAttempts || [];
          j.introAttempts.push({
            n: j.introAttempts.length + 1,
            status,
            feedback,
            date: new Date().toISOString(),
          });
          logInternal(
            d,
            "note",
            "Outreach attempt " +
              j.introAttempts.length +
              " (" +
              status +
              ") logged on " +
              j.zohoId,
            id,
          );
        }),

      logAttemptN: (id, n) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          const label = ordinal(n) + " attempt";
          j.introAttempts = (j.introAttempts || []).filter((a) => a.n !== n);
          j.introAttempts.push({
            n,
            status: "Call done",
            feedback: label,
            date: new Date().toISOString(),
          });
          j.introAttempts.sort((a, b) => a.n - b.n);
          logInternal(d, "note", label + " marked done on " + j.zohoId, id);
          sync(j, "attempt", attemptLabel("intro", n));
        }),

      // Same as logAttemptN, for the confirmation call — kept in its own list.
      logConfirmAttemptN: (id, n) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          const label = ordinal(n) + " confirmation attempt";
          j.confirmAttempts = (j.confirmAttempts || []).filter((a) => a.n !== n);
          j.confirmAttempts.push({
            n,
            status: "Call done",
            feedback: label,
            date: new Date().toISOString(),
          });
          j.confirmAttempts.sort((a, b) => a.n - b.n);
          logInternal(d, "note", label + " marked done on " + j.zohoId, id);
          sync(j, "attempt", attemptLabel("confirm", n));
        }),

      // Same again for the post-move call (Asana 1219375712610643) — its own list.
      logPostMoveAttemptN: (id, n) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          const label = ordinal(n) + " post-move attempt";
          j.postMoveAttempts = (j.postMoveAttempts || []).filter((a) => a.n !== n);
          j.postMoveAttempts.push({
            n,
            status: "Call done",
            feedback: label,
            date: new Date().toISOString(),
          });
          j.postMoveAttempts.sort((a, b) => a.n - b.n);
          logInternal(d, "note", label + " marked done on " + j.zohoId, id);
          sync(j, "attempt", attemptLabel("post-move", n));
        }),

      // ---- callbacks ---------------------------------------------------------
      saveCallback: (id, date, time) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.callbackDate = date;
          j.callbackTime = time;
          logInternal(
            d,
            "reminder",
            "Callback scheduled for " +
              j.zohoId +
              " on " +
              fmtDate(date) +
              (time ? " at " + time : ""),
            id,
          );
        }),

      // ---- escalations -------------------------------------------------------
      // stage "delivery": raised on an LD delivery day, so the backend posts it to the
      // delivery branch's operations channel instead of the pickup branch's.
      raiseEscalation: (id, type, note, stage) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          d.escalations.push({
            id: uid(),
            jobId: id,
            jobLabel: j.zohoId + " · " + j.clientName,
            type,
            note,
            status: "open",
            ...(stage ? { stage } : {}),
            createdAt: new Date().toISOString(),
          });
          logInternal(
            d,
            "note",
            "Escalation raised on " + j.zohoId + " (" + type + "): " + note,
            id,
          );
          // A Zoho note too, so the escalation is on the lead (Asana 1220099429071097, 4.5).
          sync(j, "note", "Escalation raised (" + (type === "ops" ? "Operations" : "Sales") + "): " + note);
          if (effects.postEscalation)
            effects.postEscalation({
              jobLabel: j.zohoId + " · " + j.clientName,
              zohoId: j.zohoId,
              leadId: j.leadId || null, // lets the backend DM the salesperson as well
              type,
              note,
              ...(stage ? { stage } : {}),
            });
        }),

      // The MC summarises how the escalation was handled and resolves every open
      // escalation on the job at once.
      resolveJobEscalations: (id, notes) => {
        if (!state.escalations.some((e) => e.jobId === id && e.status === "open")) return;
        return mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          const text = String(notes || "").trim();
          const now = new Date().toISOString();
          const open = d.escalations.filter((e) => e.jobId === id && e.status === "open");
          if (!open.length) return;
          open.forEach((e) => {
            e.status = "resolved";
            e.resolvedAt = now;
            e.resolutionNote = text;
          });
          j.postEscalationNotes = text;
          pushNote(d, j, "Escalation resolved: " + text);
          logInternal(d, "note", "Escalation resolved on " + j.zohoId + ": " + text, id);
          sync(j, "note", "Escalation resolved: " + text);
        });
      },

      // ---- post-move ongoing issues -----------------------------------------
      // issue = { text, remDate?, remTime?, remNote? }. Newest first.
      addPmIssue: (id, issue) => {
        if (!String((issue && issue.text) || "").trim()) return;
        return mutate((d) => {
          const j = job(d, id);
          const text = String((issue && issue.text) || "").trim();
          if (!j || !text) return;
          const remDate = (issue && issue.remDate) || "";
          const remTime = remDate ? (issue && issue.remTime) || "" : "";
          const remNote = remDate ? String((issue && issue.remNote) || "").trim() : "";
          const issueId = uid();
          j.pmIssues = Array.isArray(j.pmIssues) ? j.pmIssues : [];
          j.pmIssues.unshift({
            id: issueId,
            text,
            author: "MC",
            date: new Date().toISOString(),
            status: "open",
            remDate,
            remTime,
            remNote,
            comments: [],
          });
          if (remDate) pushPmReminder(d, j, issueId, text, remDate, remTime, remNote);
          pushPmNote(j, "Post-move issue logged: " + text);
          logInternal(d, "note", "Post-move issue logged on " + j.zohoId + ": " + text, id);
        });
      },

      togglePmIssue: (id, issueId) =>
        mutate((d) => {
          const j = job(d, id);
          const issue = pmIssue(j, issueId);
          if (!issue) return;
          issue.status = issue.status === "resolved" ? "open" : "resolved";
          logInternal(d, "note", "Post-move issue " + issue.status + " on " + j.zohoId, id);
        }),

      addPmIssueComment: (id, issueId, text) =>
        mutate((d) => {
          const j = job(d, id);
          const issue = pmIssue(j, issueId);
          const t = String(text || "").trim();
          if (!issue || !t) return;
          issue.comments = Array.isArray(issue.comments) ? issue.comments : [];
          issue.comments.push({ text: t, author: "MC", date: new Date().toISOString() });
          pushPmNote(j, "Issue comment: " + t);
        }),

      // reminder = { date, time?, note? } — also lands on the Reminders page.
      setPmIssueReminder: (id, issueId, reminder) =>
        mutate((d) => {
          const j = job(d, id);
          const issue = pmIssue(j, issueId);
          const date = reminder && reminder.date;
          if (!issue || !date) return;
          issue.remDate = date;
          issue.remTime = reminder.time || "";
          issue.remNote = String(reminder.note || "").trim();
          pushPmReminder(d, j, issueId, issue.text, date, issue.remTime, issue.remNote);
        }),

      // The MC declares a short, no-action client text "reviewed": it leaves
      // Needs Attention immediately (the backend also excludes it on refresh).
      markTextReviewed: (id, messageId, text) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j || messageId == null) return;
          if (!Array.isArray(j.reviewedTexts)) j.reviewedTexts = []; // []-vs-{} trap
          if (!j.reviewedTexts.includes(messageId)) j.reviewedTexts.push(messageId);
          const all = [].concat(
            (d.unanswered24h || {}).texts || [],
            (d.unanswered48h || {}).texts || [],
            (d.unansweredOpen || {}).texts || [],
          );
          const item = all.find((t) => String(t.messageId) === String(messageId));
          const preview = String(text || (item && item.text) || "").slice(0, 60);
          const note = "Client text reviewed — no action needed: \"" + preview + "\"";
          pushNote(d, j, note);
          logInternal(d, "note", note + " (" + j.zohoId + ")", id);
        }),

      // ---- templates / team notes -------------------------------------------
      sendTemplate: (id, template) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          j.templatesSent = j.templatesSent || [];
          if (j.templatesSent.includes(template)) return;
          j.templatesSent.push(template);
          logExternal(
            d,
            "email",
            'Sent "' + template + '" document to ' + j.clientName + " (" + j.zohoId + ")",
            id,
            false,
          );
          if (effects.sendDocument && j.leadId)
            effects.sendDocument({ job: j, docType: template });
        }),

      sendChannelTemplate: (id, channel, template) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          logExternal(
            d,
            channel,
            'Sent "' +
              template +
              '" ' +
              (channel === "text" ? "text" : "email") +
              " template to " +
              j.clientName +
              " (" +
              j.zohoId +
              ")",
            id,
            false,
          );
          if (channel === "text" && effects.sendClientSms && j.phone)
            effects.sendClientSms({
              phone: j.phone,
              template,
              client: j.clientName,
              moveDate: j.moveDate,
            });
          if (channel === "email" && effects.sendClientEmail && j.email)
            effects.sendClientEmail({
              email: j.email,
              template,
              client: j.clientName,
              moveDate: j.moveDate,
              leadId: j.leadId,
            });
        }),

      // Send the coordinator-edited message from the compose popup and record
      // WHO sent it + WHEN on the job (job.sentMessages, persisted).
      sendComposedMessage: (id, payload) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          const { channel, to, subject, body, template, sentBy } = payload || {};
          if (!body || !to) return;

          j.sentMessages = Array.isArray(j.sentMessages) ? j.sentMessages : [];
          j.sentMessages.push({
            id: uid(),
            channel,
            to,
            subject: subject || "",
            body,
            template: template || "",
            sentBy: sentBy || "MC",
            sentAt: new Date().toISOString(),
          });

          logExternal(
            d,
            channel,
            (channel === "email" ? "Email sent to " : "Text sent to ") +
              j.clientName + " (" + j.zohoId + ") by " + (sentBy || "MC"),
            id,
            false,
          );

          if (channel === "text" && effects.sendClientSms)
            effects.sendClientSms({ phone: to, body });
          if (channel === "email" && effects.sendClientEmail)
            effects.sendClientEmail({ email: to, subject, body, leadId: j.leadId });
        }),

      sendTeamNote: (id, target, text) =>
        mutate((d) => {
          const j = job(d, id);
          if (!j) return;
          logInternal(
            d,
            "note",
            (target === "sales" ? "Note to sales rep on " : "Note to operations on ") +
              j.zohoId +
              ": " +
              text,
            id,
          );
          j.teamNoteDraft = "";
          if (target === "sales") j.salesRepDraft = "";
          else j.opsDraft = "";
          if (effects.postTeamNote && j.leadId)
            effects.postTeamNote({ leadId: j.leadId, target, text });
        }),
    };
  }, [state, persist, effects]);
